import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { GitBranch, Clock, Newspaper, Wrench, ArrowRight, Compass } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { PageTransition } from "@/components/PageTransition";

const TILES = [
  { key: "tools", to: "/tools", title: "Learn a Tool", tag: "Hands-on", featured: true,
    desc: "Full, hands-on courses on ChatGPT, Gemini, Claude and 39+ AI tools — basics to real projects.",
    icon: Wrench, color: "bg-primary text-primary-foreground" },
  { key: "mindmap", to: "/universe/mindmap", title: "AI Mind Map", tag: "Big picture",
    desc: "The whole world of AI on one map — concepts, models, agents and the future, branch by branch.",
    icon: GitBranch, color: "bg-brand-cyber text-white" },
  { key: "timeline", to: "/universe/timeline", title: "AI Timeline", tag: "History",
    desc: "From 1950 to AGI — how AI began, where it is now, and where it's heading next.",
    icon: Clock, color: "bg-brand-pink text-white" },
  { key: "whats-new", to: "/whats-new", title: "What's New in AI", tag: "Fresh",
    desc: "The latest tools, model releases and breakthroughs — hand-picked and explained simply.",
    icon: Newspaper, color: "bg-brand-tangerine text-brand-ink" },
];

function Tile({ t, index, onOpen }) {
  const Icon = t.icon;
  return (
    <motion.button
      data-testid={`explore-tile-${t.key}`}
      onClick={() => onOpen(t.to)}
      initial={{ opacity: 0, y: 18 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: Math.min(index * 0.07, 0.4), ease: [0.22, 1, 0.36, 1] }}
      className={`group text-left bg-card border border-border rounded-2xl shadow-brutal brutal-press p-6 flex flex-col ${
        t.featured ? "sm:col-span-2 lg:col-span-2 beam-top relative" : ""
      }`}
    >
      <div className="flex items-center justify-between">
        <span className={`grid place-items-center h-12 w-12 rounded-xl ${t.color}`}>
          <Icon className="h-6 w-6" />
        </span>
        <span className="font-mono text-[10px] font-bold uppercase tracking-widest border border-border rounded-full px-2.5 py-0.5 text-muted-foreground">{t.tag}</span>
      </div>
      <h3 className="mt-4 font-heading font-extrabold tracking-tight text-xl">{t.title}</h3>
      <p className="mt-1.5 text-sm font-medium text-muted-foreground flex-1">{t.desc}</p>
      <span className="mt-4 inline-flex items-center gap-1 font-mono text-xs font-bold uppercase tracking-wider text-primary group-hover:gap-2 transition-all">
        Open <ArrowRight className="h-4 w-4" />
      </span>
    </motion.button>
  );
}

export default function Explore() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-background grid-paper">
      <Navbar />
      <PageTransition className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 pb-24">
        <div data-testid="explore-header">
          <p className="font-mono text-xs font-bold uppercase tracking-widest text-primary inline-flex items-center gap-1.5">
            <Compass className="h-4 w-4" /> Beyond your path
          </p>
          <h1 className="mt-2 font-heading font-extrabold tracking-tight text-4xl sm:text-5xl">Explore</h1>
          <p className="mt-2 font-medium max-w-2xl text-muted-foreground text-base md:text-lg">
            Tools, the big picture of AI, its history, and what's new — everything beyond your daily lessons.
          </p>
        </div>

        <div className="mt-8 grid sm:grid-cols-2 lg:grid-cols-2 gap-4" data-testid="explore-tiles">
          {TILES.map((t, i) => <Tile key={t.key} t={t} index={i} onOpen={navigate} />)}
        </div>
      </PageTransition>
    </div>
  );
}
